/**
 * First-touch attribution: where this visitor came from. Captured once, on the
 * first visit that carries any campaign data, and sent to the API with the
 * guest session and at sign-in (apps/api/src/marketing). Ad cookies (_fbp,
 * _fbc, _ttp, _ga) are read when sending, since the pixels set them later.
 *
 * The Android app passes the Play install referrer on its first launch as
 * `?install_referrer=<utm_source=…&gclid=…>`; its tags count like URL tags.
 */
import { adConsent } from './consent.js';

const KEY = 'loomia.attribution';

type Attr = Record<string, string>;

const URL_KEYS: Record<string, string> = {
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_content: 'utmContent',
  utm_term: 'utmTerm',
  gclid: 'gclid',
  gbraid: 'gbraid',
  wbraid: 'wbraid',
  fbclid: 'fbclid',
  ttclid: 'ttclid',
};

function read(): Attr | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Attr) : null;
  } catch {
    return null;
  }
}

function pick(params: URLSearchParams, into: Attr): void {
  for (const [param, key] of Object.entries(URL_KEYS)) {
    const v = params.get(param);
    if (v) into[key] = v.slice(0, 500);
  }
}

/** Call once at boot, before the router rewrites the URL. */
export function captureAttribution(): void {
  if (read()) return; // first touch wins
  const params = new URLSearchParams(window.location.search);
  const hashQuery = window.location.hash.split('?')[1];
  if (hashQuery) new URLSearchParams(hashQuery).forEach((v, k) => params.set(k, v));

  const found: Attr = {};
  pick(params, found);
  const installReferrer = params.get('install_referrer');
  if (installReferrer) {
    found.installReferrer = installReferrer.slice(0, 500);
    pick(new URLSearchParams(installReferrer), found);
  }
  const ref = document.referrer && !document.referrer.startsWith(window.location.origin) ? document.referrer : '';
  if (ref) found.referrer = ref.slice(0, 500);
  if (!Object.keys(found).length) return; // direct visit: wait for a tagged one

  found.landingUrl = `${window.location.origin}${window.location.pathname}`;
  found.firstSeenAt = new Date().toISOString();
  try {
    localStorage.setItem(KEY, JSON.stringify(found));
  } catch {
    // Private mode: attribution is best effort.
  }
}

function cookie(name: string): string | undefined {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]!) : undefined;
}

/** What the API stores for this visitor (first touch + current ad cookies + consent). */
export function attributionPayload(): Record<string, string | boolean> {
  const out: Record<string, string | boolean> = { ...(read() ?? {}) };
  const fbp = cookie('_fbp');
  const fbc = cookie('_fbc');
  const ttp = cookie('_ttp');
  // _ga = GA1.1.<client id: two dot-separated numbers>
  const ga = cookie('_ga')?.split('.').slice(-2).join('.');
  if (fbp) out.fbp = fbp;
  if (fbc) out.fbc = fbc;
  if (ttp) out.ttp = ttp;
  if (ga) out.gaClientId = ga;
  out.adConsent = adConsent();
  return out;
}
